<template>
  <div v-if="showDialog">
    <div
      v-show="isOpen"
      class="dialog-overlay dialog-wrap__event"
      :class="customClass"
      :data-theme="theme"
      :style="{'z-index':dialogZIndex}"
      @click="clickOverlay"
    >
      <!-- 根据 componentName 条件渲染对应组件 -->
      <template v-if="componentName">
        <component
          :is="componentName"
          :content="content"
          :allow-html="allowHtml"
          :lang="lang"
          :show-header="showHeader"
          :content-stop-propagation="contentStopPropagation"
          :num-position="numPosition"
          @close="closeDialog"
          @confirm="confirm"
        >
          <template #title>
            <div
              v-if="lang?.title||$slots.title"
              class="dialog-box__header"
            >
              <slot name="title">
                <div class="dialog-box__title">
                  {{ lang.title }}
                </div>
              </slot>
            </div>
          </template>
          <template #content>
            <slot name="content"></slot>
          </template>
          <template #tableDesc>
            <slot name="tableDesc"></slot>
          </template>
          <template #close>
            <div
              v-if="showClose"
              class="dialog-box__close"
              @click="closeDialog"
            ></div>
          </template>
          <template #footer>
            <div
              v-if="lang.cancelText||lang.confirmText||$slots.footer"
              class="dialog-box__footer"
            >
              <slot name="footer">
                <button
                  v-if="lang.cancelText"
                  class="dialog-btn dialog-btn__cancel"
                  @click="cancel"
                >
                  <span
                    :data-txt="lang.cancelText | decodeHtml"
                    class="txt"
                  >{{ lang.cancelText | decodeHtml }}</span>
                </button>
                <button
                  v-if="lang.confirmText"
                  class="dialog-btn dialog-btn__confirm"
                  @click="confirm"
                >
                  <span
                    :data-txt="lang.confirmText | decodeHtml"
                    class="txt"
                  >{{ lang.confirmText | decodeHtml }}</span>
                </button>
              </slot>
            </div>
          </template>
        </component>
      </template>
    </div>
  </div>
</template>

<script>
import CommonContent from './components/CommonContent.vue'
import TableContent from './components/TableContent.vue'
import RewardContent from './components/RewardContent.vue'
// 全局弹窗计数器（用于追踪真正打开的弹窗数量）
let openDialogCount = 0

// 常量定义
const Z_INDEX_INCREMENT = 5 // 遮罩层每次增加的层级
const Z_INDEX_DIALOG_OFFSET = 1 // 弹窗相对遮罩层的偏移
const ANIMATION_DURATION = 150 // 动画时长（毫秒）

const DialogTypeEnum = {
  /** 通用 */
  COMMON: 'CommonContent',
  /** 表格 */
  TABLE: 'TableContent',
  /** 图文 */
  Reward: 'RewardContent',
}
export default {
  name: 'EventDialog',
  components: {
    CommonContent,
    TableContent,
    RewardContent,
  },
  filters: {
    // 过滤器：将字符串转为驼峰格式
    decodeHtml(text) {
      try {
        const parser = new DOMParser()
        const decodedString = parser.parseFromString(
    `<!doctype html><body>${text}`,
    'text/html',
        ).body.textContent

        return decodedString || text
      } catch (error) {
        return text
      }
    },
  },
  model: {
    prop: 'visible',
    event: 'changeVisible',
  },
  props: {
    // 控制是否展示
    visible: {
      type: Boolean,
      default: false,
    },
    // 弹窗类型
    componentName: {
      type: String,
      default: DialogTypeEnum.COMMON,
    },
    // 数据内容，可能是表格数据、一段文本、一段html
    // eslint-disable-next-line
      content: null,
    // 是否渲染html 为 true 的情况则使用v-html 渲染message字段
    allowHtml: {
      type: Boolean,
      default: false,
    },
    // 多语言字段
    lang: {
      type: Object,
      default: () => ({}),
    },
    // 点击遮罩层关闭
    closeOnClickOverlay: {
      type: Boolean,
      default: true,
    },
    // 是否锁定屏幕
    lockScroll: {
      type: Boolean,
      default: false,
    },
    // 自定义弹窗最外层类名
    customClass: {
      type: String,
      default: null,
    },
    // 元素要挂载的位置
    getContainer: {
      type: Function,
      default: () => document.body,
    },
    // 主题  目前外部都没有用到，暂时先隐藏
    // theme: {
    //   type: String,
    //   default: null,
    // },
    // 遮罩层、弹窗初始层级
    zIndex: {
      type: Number,
      default: 1000,
    },
    // 是否显示表头，仅表格弹窗有用
    showHeader: {
      type: Boolean,
      default: true,
    },
    // 关闭弹窗时是否销毁元素
    destroyOnClose: {
      type: Boolean,
      default: true,
    },
    // 是否显示关闭按钮
    showClose: {
      type: Boolean,
      default: true,
    },
    // 是否需要自定义取消事件
    customCancel: {
      type: Boolean,
      default: false,
    },
    // 点击遮罩层是否阻止冒泡
    overlayStopPropagation: {
      type: Boolean,
      default: true,
    },
    // 点击内容区域是否阻止冒泡
    contentStopPropagation: {
      type: Boolean,
      default: true,
    },
    // 奖励数量的位置 仅在奖励弹窗场景有效
    numPosition: {
      type: String,
      default: 'bottom-end',
    },
    // 是否禁用动画效果
    disableAnimation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      imageContentIndex: 0, // 获取图片切换时的索引值
      dialogZIndex: 0, // 弹窗内容的索引值
      isDoingCloseAnimate: false, // 是否正在执行关闭操作，防止冲突
      showDialog: false, // 因为只用v-show来控制的话会存在一个问题：引入时界面就存在节点，因此需要一个属性来控制
      hasCountedAsOpen: false, // 标记是否已经计入打开计数
      openRafId: null, // 打开动画的 RAF ID
      closeRafId: null, // 关闭动画的 RAF ID
      originalBodyPadding: null, // 保存原始 body padding，null 表示未保存
      originalBodyOverflow: null, // 保存原始 body overflow，null 表示未保存
    }
  },
  watch: {
    visible: {
      handler: function(val, oldVal) {
        if (val) {
          this.isOpen = true
          this.showDialog = true
          this.openDialog()
        } else if (!val && oldVal) {
          // 当值为false时，调用关闭弹窗的方法，因为还有动画需要执行
          this.closeDialog()
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    // SSR 兼容性检查
    if (typeof document === 'undefined') return

    // 1. 取消所有动画
    if (this.openRafId) {
      cancelAnimationFrame(this.openRafId)
      this.openRafId = null
    }
    if (this.closeRafId) {
      cancelAnimationFrame(this.closeRafId)
      this.closeRafId = null
    }

    // 2. 清理计数
    if (this.hasCountedAsOpen) {
      openDialogCount--
      this.hasCountedAsOpen = false
    }

    // 3. 移除元素
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }

    // 4. 清理遮罩层
    this.judgeRemoveMask()

    // 5. 恢复滚动
    if (this.lockScroll) {
      const eventDialogEls = document.getElementsByClassName('dialog-wrap__event')
      const allHidden = Array.from(eventDialogEls).every(el => el.style.display === 'none')
      if (!eventDialogEls.length || allHidden) {
        document.body.style.paddingRight = this.originalBodyPadding !== null ? this.originalBodyPadding : ''
        document.body.style.overflow = this.originalBodyOverflow !== null ? this.originalBodyOverflow : ''
      }
    }
  },
  methods: {
    decodeHtml(html) {
      const txt = document.createElement('textarea')
      txt.innerHTML = html
      return txt.value
    },

    // 打开弹窗判断是否需要添加遮罩层
    judgeOpenMask() {
      // SSR 兼容性检查
      if (typeof document === 'undefined') return

      const eventDialogMaskEl = document.getElementById('event-dialog-mask')
      // 判断是否存在遮罩层
      if (eventDialogMaskEl) {
        // 如果存在，那么需要设置一下遮罩层的层级，因为层级需要大于上一个弹窗的层级，才可以真正有效遮住
        const zIndex = Number(eventDialogMaskEl.style.zIndex) + Z_INDEX_INCREMENT
        eventDialogMaskEl.style.zIndex = zIndex
        eventDialogMaskEl.classList.remove('dialog-wrap__animation-close')
        eventDialogMaskEl.classList.add('dialog-wrap__animation-open')
        this.dialogZIndex = zIndex + Z_INDEX_DIALOG_OFFSET
      } else {
        // 如果不存在的情况，那么就创建一个弹窗出来
        const maskElement = document.createElement('div')
        // 设置类名
        maskElement.className = 'event-dialog__mask'
        // 设置 ID
        maskElement.id = 'event-dialog-mask'
        maskElement.classList.add('dialog-wrap__animation-open')
        maskElement.style.zIndex = this.zIndex
        this.dialogZIndex = this.zIndex + Z_INDEX_DIALOG_OFFSET
        document.body.appendChild(maskElement)
      }
    },

    // 更新遮罩层状态（添加或移除关闭动画）
    updateMaskAnimation(shouldClose) {
      // SSR 兼容性检查
      if (typeof document === 'undefined') return

      const eventDialogMaskEl = document.getElementById('event-dialog-mask')
      if (!eventDialogMaskEl) return

      if (shouldClose) {
        // 开始关闭动画
        eventDialogMaskEl.classList.remove('dialog-wrap__animation-open')
        eventDialogMaskEl.classList.add('dialog-wrap__animation-close')
      } else {
        // 保持打开状态
        eventDialogMaskEl.classList.remove('dialog-wrap__animation-close')
        eventDialogMaskEl.classList.add('dialog-wrap__animation-open')
      }
    },

    // 是否需要移除遮罩层元素
    judgeRemoveMask() {
      // SSR 兼容性检查
      if (typeof document === 'undefined') return

      this.$nextTick(() => {
        const eventDialogEls = document.getElementsByClassName('dialog-wrap__event')
        const eventDialogMaskEl = document.getElementById('event-dialog-mask')

        // 安全检查：遮罩层可能已被移除
        if (!eventDialogMaskEl) return

        const eventDialogLength = eventDialogEls.length
        let allNotShow = true // 是否全部为关闭

        for (let i = 0; i < eventDialogLength; i++) {
          if (eventDialogEls[i].style.display !== 'none') {
            allNotShow = false
            break
          }
        }

        // 判断是否还有弹窗元素，不存在弹窗的话就将其移出
        if (!eventDialogLength || allNotShow) {
          // 安全移除：再次检查父节点是否存在
          if (eventDialogMaskEl.parentNode) {
            document.body.removeChild(eventDialogMaskEl)
          }
        } else {
          // 获取当前弹窗的最高层级，如果存在的话，就获取当前所有元素中最高层级的元素，并修改遮罩层的层级
          const maxZIndex = Array.from(eventDialogEls).reduce(function(max, obj) {
            return obj.style.display !== 'none' && Number(obj.style.zIndex) > max ? Number(obj.style.zIndex) : max
          }, -Infinity)
          // 遮罩层的层级-1
          if (maxZIndex > -Infinity) {
            eventDialogMaskEl.style.zIndex = maxZIndex - Z_INDEX_DIALOG_OFFSET
          }
        }
      })
    },

    // 打开弹窗执行的回调
    openDialog() {
      this.judgeOpenMask()

      // 增加全局计数（确保只计数一次）
      if (!this.hasCountedAsOpen) {
        openDialogCount++
        this.hasCountedAsOpen = true
      }

      // 获取容器元素
      let container = document.body
      if (typeof this.getContainer === 'function') {
        // 创建弹窗组件的根元素
        container = this.getContainer() || document.body
      }
      this.$emit('open')
      this.onLockScroll()
      this.$nextTick(() => {
        this.$el && container.appendChild(this.$el)
        this.openAnimate()
      })
    },

    // 界面打开时的动画
    openAnimate() {
      // 清理之前的动画
      if (this.openRafId) {
        cancelAnimationFrame(this.openRafId)
        this.openRafId = null
      }

      const element = this.$el && this.$el.querySelector('.dialog-box__animation')
      if (!element) {
        this.$emit('opened')
        return
      }

      // 如果禁用动画，直接设置最终状态
      if (this.disableAnimation) {
        element.style.transform = 'scale3d(1,1,1)'
        element.style.opacity = 1
        this.$emit('opened')
        return
      }

      let startTime
      const that = this
      function animate(timestamp) {
        // 检查组件是否已销毁
        if (that._isDestroyed) {
          return
        }

        if (!startTime) {
          startTime = timestamp
        }

        const progress = timestamp - startTime

        // 根据时间进度计算动画的状态
        const scale = Math.min(progress / ANIMATION_DURATION, 1)

        // 应用动画状态
        element.style.transform = `scale3d(${scale},${scale},${scale})`
        element.style.opacity = scale

        if (progress < ANIMATION_DURATION) {
          that.openRafId = requestAnimationFrame(animate)
        } else {
          that.openRafId = null
          that.$emit('opened')
        }
      }
      this.openRafId = requestAnimationFrame(animate)
    },

    // 关闭弹窗的动画
    closeAnimate() {
      if (this.isDoingCloseAnimate) return

      // 清理之前的动画
      if (this.closeRafId) {
        cancelAnimationFrame(this.closeRafId)
        this.closeRafId = null
      }

      this.$emit('close') // 关闭弹窗的回调

      // 先减少计数（确保只减少一次）
      if (this.hasCountedAsOpen) {
        openDialogCount--
        this.hasCountedAsOpen = false
      }

      // 立即检查是否需要开始遮罩层关闭动画
      const shouldCloseMask = openDialogCount === 0 // 如果没有其他打开的弹窗，则关闭遮罩层
      this.updateMaskAnimation(shouldCloseMask)

      const element = this.$el && this.$el.querySelector('.dialog-box__animation')
      if (!element) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[EventDialog] Animation element not found in closeAnimate')
        }
        // 即使没有动画元素也要完成关闭流程
        this.isOpen = false
        this.$emit('changeVisible', false)
        this.$nextTick(() => {
          this.onLockScroll()
          this.$emit('closed')
          this.judgeRemoveMask()
          if (this.destroyOnClose) {
            this.$nextTick(() => {
              if (this.$el && this.$el.parentNode) {
                this.$el.parentNode.removeChild(this.$el)
              }
              this.showDialog = false
            })
          }
        })
        return
      }

      // 如果禁用动画，直接设置最终状态并完成关闭流程
      if (this.disableAnimation) {
        element.style.transform = 'scale3d(0,0,0)'
        element.style.opacity = 0
        this.isOpen = false
        this.$emit('changeVisible', false)
        this.$nextTick(() => {
          this.onLockScroll()
          this.$emit('closed')
          this.judgeRemoveMask()
          if (this.destroyOnClose) {
            this.$nextTick(() => {
              if (this.$el && this.$el.parentNode) {
                this.$el.parentNode.removeChild(this.$el)
              }
              this.showDialog = false
            })
          }
        })
        return
      }

      let startTime
      const that = this
      that.isDoingCloseAnimate = true

      function doCloseAnimate(timestamp) {
        // 检查组件是否已销毁
        if (that._isDestroyed) {
          return
        }

        if (!startTime) startTime = timestamp
        const elapsed = timestamp - startTime

        // 根据时间计算动画的进度
        const progress = Math.min(elapsed / ANIMATION_DURATION, 1)

        // 根据进度更新元素的样式
        const scale = 1 - progress // 从1到0的缩放
        element.style.transform = `scale3d(${scale},${scale},${scale})`
        element.style.opacity = scale

        // 判断动画是否结束
        if (progress < 1) {
          that.closeRafId = requestAnimationFrame(doCloseAnimate)
        } else {
          that.closeRafId = null
          // 动画结束后执行的操作
          that.isOpen = false
          that.$emit('changeVisible', false) // v-model 会用到的属性

          // 使用 $nextTick 确保 DOM 更新后再处理后续逻辑
          that.$nextTick(() => {
            that.onLockScroll()
            that.isDoingCloseAnimate = false
            that.$emit('closed')

            // 移除遮罩层的判断
            that.judgeRemoveMask()

            // 销毁元素
            if (that.destroyOnClose) {
              that.$nextTick(() => {
                if (that.$el && that.$el.parentNode) {
                  that.$el.parentNode.removeChild(that.$el)
                }
                that.showDialog = false
              })
            }
          })
        }
      }
      // 启动动画
      this.closeRafId = requestAnimationFrame(doCloseAnimate)
    },

    // 关闭弹窗执行的回调
    closeDialog() {
      this.closeAnimate()
    },

    cancel() {
      if (this.customCancel) {
        this.$emit('cancel')
      } else {
        this.closeDialog()
      }
    },

    // 点击确定的方法 点击确定的情况不论什么方式使用组件，务必要主动关闭弹窗，否则无法自动关闭
    confirm(data) {
      this.$emit('confirm', data)
    },

    // 点击遮罩层方法
    clickOverlay(event) {
      if (this.overlayStopPropagation) {
        event.stopPropagation()
      }
      if (this.closeOnClickOverlay) {
        // 点击落在 dialog-box__animation 内部时不关闭
        const dialogBox = this.$el && this.$el.querySelector('.dialog-box__animation')
        if (!dialogBox || !dialogBox.contains(event.target)) {
          this.closeDialog()
        }
      }
    },

    // 锁定滚动条；防止打开时界面抖动
    onLockScroll() {
      if (this.lockScroll && this.isOpen) {
        // 保存原始值（null 表示尚未保存）
        if (this.originalBodyPadding === null) {
          this.originalBodyPadding = document.body.style.paddingRight
        }
        if (this.originalBodyOverflow === null) {
          this.originalBodyOverflow = document.body.style.overflow
        }

        // 获取当前滚动条的宽度
        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
        // 给界面加一个对应滚动条宽度的padding  防止抖动
        document.body.style.paddingRight = scrollBarWidth + 'px'
        // 使界面不可以滚动
        document.body.style.overflow = 'hidden'
      } else {
        this.$nextTick(() => {
          // v-show 仅隐藏元素（display:none），需判断是否所有弹窗都已隐藏
          const eventDialogEls = document.getElementsByClassName('dialog-wrap__event')
          const allHidden = Array.from(eventDialogEls).every(el => el.style.display === 'none')
          if (!eventDialogEls.length || allHidden) {
            // 恢复原始值
            document.body.style.paddingRight = this.originalBodyPadding !== null ? this.originalBodyPadding : ''
            document.body.style.overflow = this.originalBodyOverflow !== null ? this.originalBodyOverflow : ''
            this.originalBodyPadding = null
            this.originalBodyOverflow = null
          }
        })
      }
    },
  },

}
</script>

<style lang="scss">
  @import './dialog.scss';
  @keyframes openGradientAnimation {
    0% {
      background-color: rgba(0, 0, 0, 0); /* 初始背景色 */
    }

    100% {
      background-color: var(--bg-dialog-mask,rgba(0, 0, 0, 0.5)); /* 最终背景色 */
    }
  }

  @keyframes closeGradientAnimation {
    0% {
      background-color:var(--bg-dialog-mask,rgba(0, 0, 0, 0.5)); /* 初始背景色 */
    }

    100% {
      background-color: rgba(0, 0, 0, 0); /* 最终背景色 */
    }
  }
</style>

<style lang="scss">
  //有新的主题就往这里引入
  @import './theme/lm.scss';
  @import './theme/lo.scss';
</style>
